import { type IAdminWithdrawalListItem } from '@mahawi/eshop-common/src/types';
import { Divider } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import { EDateFormat } from '../../fragments/date-format';
import { useAppDispatch } from '../../hooks/use-app-dispatch';
import { type RootState } from '../../reducers';
import { withdrawalsLoadByOrder } from '../../reducers/withdrawal/reducer';
import Link from '../link';
import TableComponent from '../table';
import WithdrawalStatusTag from './status-tag';

type TOrderWithdrawalRow = IAdminWithdrawalListItem & { key: string };

const columns: ColumnsType<TOrderWithdrawalRow> = [
  {
    title: 'Reference',
    dataIndex: 'referenceCode',
    render: (
      referenceCode: string,
      record: TOrderWithdrawalRow,
    ): React.ReactElement => (
      <Link to={`/orders/withdrawal/${record.uuid}`} label={referenceCode} />
    ),
  },
  {
    title: 'Received',
    dataIndex: 'receivedAt',
    render: (receivedAt: string): string =>
      dayjs(receivedAt).format(EDateFormat.DATE_TIME),
  },
  {
    title: 'Status',
    dataIndex: 'status',
    render: (
      _status: string,
      record: TOrderWithdrawalRow,
    ): React.ReactElement => <WithdrawalStatusTag status={record.status} />,
  },
  {
    title: 'Refunded',
    render: (_value: string, record: TOrderWithdrawalRow): string =>
      record.refundedAmount === null
        ? ''
        : `${record.refundedAmount} ${record.currencyIsoCode ?? ''}`.trim(),
  },
];

function OrderWithdrawals({
  orderUUID,
}: {
  orderUUID: string;
}): React.ReactElement | null {
  const dispatch = useAppDispatch();
  const withdrawals = useSelector((s: RootState) => s.Withdrawal.byOrder);

  useEffect((): void => {
    dispatch(withdrawalsLoadByOrder(orderUUID));
  }, [dispatch, orderUUID]);

  if (!withdrawals || withdrawals.length === 0) {
    return null;
  }

  return (
    <>
      <Divider titlePlacement="left">Withdrawals</Divider>
      <TableComponent
        columns={columns as Parameters<typeof TableComponent>[0]['columns']}
        dataSource={withdrawals.map((withdrawal) => ({
          key: withdrawal.uuid,
          ...withdrawal,
        }))}
        isLoading={false}
      />
    </>
  );
}

export default OrderWithdrawals;
